import { CellClassParams, ColDef } from "ag-grid-community";
import { RowDef } from "../../components/ag-grid-extensions/models/RowDef";
import i18n from "../../services/localization-service/i18n";
import { IDateRange } from "../../common/models/IDateRange";
import { IAlarmAndRecipientsSetpointInformation, IAlarmRecipientDto, ISetPointListReportModel } from "../../services/admin-report-data-service/Models";
import { DataTypeEnum } from "../../common/enums/DataTypeEnum";

const projectRow = (entry: IAlarmAndRecipientsSetpointInformation, 
                    notificationListRecipients: IAlarmRecipientDto[] | undefined,
                    directRecipients: IAlarmRecipientDto[] | undefined): RowDef => {

    const row: RowDef = {
    };

    const directRecipient = directRecipients?.length ? directRecipients[0] : undefined;

    row['setPointName'] = entry.setPointName;
    row['setPointOperator'] = entry.setPointOperator;
    row['setPointValue'] = entry.setPointDataType === DataTypeEnum.Double ? entry.setPointValue.toString() : entry.setPointTextValue;
    row['notifyonAlarm'] = entry.tagAlarmNotify ? "True" : "False";
    row['notifyonNormal'] = entry.tagClearNotify ? "True" : "False"
    row['deviceDisplayName'] = entry.deviceDisplayName;
    row['tagName'] = entry.tagName;
    row['alarmSchemeName'] = entry.alarmSchemeName;
    row['schemeDefinition'] = entry.schemeDefinition;
    row['notificationListName'] = entry.notificationListName;
    row['notificationListRecipientList'] = entry.notificationListRecipientList;
    row['v2recipientList'] = notificationListRecipients;
    row['directRecipientList'] = directRecipients;
    row['directRecipient'] = directRecipient;
    row['userFirstName'] = directRecipient?.userFirstName ?? entry.userFirstName;
    row['userLastName'] = directRecipient?.userLastName ?? entry.userLastName;
    return row;
}

const AlarmSetpointReportUtils = {

    createGridRows: function (entriesV2?: ISetPointListReportModel,

    ): RowDef[] {

        const rows: RowDef[] = entriesV2?.alarmData?.map((o) => {
            const items = entriesV2?.recipients?.filter(v => o.notificationListRecipientIDList?.includes(v.recipientID));
            const directRecipients = entriesV2?.recipients?.filter(v => o?.directRecipientID === v.recipientID);
            return projectRow(o, items, directRecipients);
        }) ?? [];

        return rows;
    },
    createGridColumns: function (): ColDef[] {


        const cols: ColDef[] = [{
            headerName: i18n.t("alarmSetpointReport.setpointName") as string,
            field: 'setPointName',
            sort: 'asc',
            pinned: 'left',
            //minWidth: 300,
            type: "text, multiline"
        }, {
            headerName: i18n.t("alarmSetpointReport.setpointOperator") as string,
            field: "setPointOperator",
            maxWidth: 148,
            type: "text"
        }, {
            headerName: i18n.t("alarmSetpointReport.setpointValue") as string,
            field: "setPointValue",
            maxWidth: 148,
            type: "number"
        }, {
            headerName: i18n.t("alarmSetpointReport.notifyonAlarm") as string,
            field: "notifyonAlarm",
            maxWidth: 148,
            type: "text"
        }, {
            headerName: i18n.t("alarmSetpointReport.notifyonNormal") as string,
            field: "notifyonNormal",
            maxWidth: 148,
            type: "text"
        }, {
            headerName: i18n.t("alarmNotificationReports.device") as string,
            field: "deviceDisplayName",
            type: "text, multiline"
        }, {
            headerName: i18n.t("alarmSetpointReport.tag") as string,
            field: "tagName",
            type: "text, multiline"
        }, {
            headerName: "Scheme Name",
            field: "alarmSchemeName",
            type: "text"
        }, {
            headerName: i18n.t("alarmSetpointReport.schemeDefinition") as string,
            field: "schemeDefinition",
            type: "text, multiline"
        }, {
            headerName: i18n.t("alarmSetpointReport.notificationListName") as string,
            field: "notificationListName",
            type: "text, multiline"
        }, {
            headerName: i18n.t("alarmSetpointReport.notificationListRecipientList") as string,
            field: "v2recipientList",
            //type: "text, multiline",            
            type: "multiline",            
            cellRenderer: 'recipientListRenderer',
            //valueGetter: (params: any) => params?.data?.notificationListRecipientList
            filterValueGetter: (params: any) => {
                const recipients = params?.data?.v2recipientList as IAlarmRecipientDto[];
                if (recipients?.length) {
                    return recipients.map(o => o.recipientTo).join(", ");
                }
                return params?.data?.directRecipient ? "(no recipients)" : "(Notification list is empty)";
            },
            comparator: (a: IAlarmRecipientDto[], b: IAlarmRecipientDto[], nodeA: any, nodeB: any) => {

                const textA = a.length > 0 ? a.map(o => o.recipientTo).join(", ") : nodeA.data.directRecipient ? "(no recipients)" : "(Notification list is empty)";
                const textB = b.length > 0 ? b.map(o => o.recipientTo).join(", ") : nodeB.data.directRecipient ? "(no recipients)" : "(Notification list is empty)";
                return textA.localeCompare(textB); // Sorts based on the visible string
            },

        }, {
            headerName: i18n.t("alarmSetpointReport.directrecipientlist") as string,
            field: "directRecipient",
            //type: "text, multiline",
            type: "multiline",
            cellRenderer: 'recipientRenderer',
            filterValueGetter: (params: any) => {
                const recipients = params?.data?.directRecipient as IAlarmRecipientDto;
                return recipients?.recipientTo ?? "(no recipients)";
            },
            comparator: (a: IAlarmRecipientDto, b: IAlarmRecipientDto) => {
                const textA = a?.recipientTo ?? "(no recipients)";
                const textB = b?.recipientTo ?? "(no recipients)";
                return textA.localeCompare(textB); // Sorts based on the visible string
            },
            //valueGetter: (params: any) => params?.data?.directRecipientList
        }, {
            headerName: i18n.t("alarmSetpointReport.userFirstName") as string,
            field: "userFirstName",
            type: "text",
            cellStyle: (params: CellClassParams) => {

                const recipient = params?.data?.directRecipient as IAlarmRecipientDto;
                return {
                    fontWeight: recipient?.userDisabled ? 'bold' : 'inherit',
                    textDecoration: recipient?.userDisabled ? 'line-through' : 'none'
                };
            },

        }, {
            headerName: i18n.t("alarmSetpointReport.userLastName") as string,
            field: "userLastName",
            type: "text",
            cellStyle: (params: CellClassParams) => {

                const recipient = params?.data?.directRecipient as IAlarmRecipientDto;
                return {
                    fontWeight: recipient?.userDisabled ? 'bold' : 'inherit',
                    textDecoration: recipient?.userDisabled ? 'line-through' : 'none'
                };
            },

        }];

        return cols;
    },

    getGridNoDataMessage(tagName: string, dateRange?: IDateRange): string {

        let message: string;

        if (dateRange && dateRange.startDate && dateRange.endDate) {
            message = i18n.t("noData.noDataForObjectWithinDateRange", {
                startDate: dateRange.startDate.toLocaleString(),
                endDate: dateRange.endDate.toLocaleString(),
                objectName: tagName
            });

        } else {
            message = i18n.t("noData.noDataForObject", { objectName: tagName });
        }

        return message;
    },
};

export default AlarmSetpointReportUtils;
